import moment from 'moment-timezone';
import * as React from 'react';
import { Box, CircularProgress, FormControl } from '@mui/material';
import { useSelector } from 'react-redux';
import { Field, FieldRenderProps, FormRenderProps, useForm, useFormState } from 'react-final-form';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import axios from 'axios';
import clsx from 'clsx';
import lodashGet from 'lodash/get';
import lodashEqual from 'lodash/isEqual';

import config from 'config';
import { PricePanel } from 'components/DateFormPanel/PricePanel';
import { PromotionPanel } from 'components/DateFormPanel/PromotionPanel';
import { Modal } from 'components/Modal/Modal';
import { ReduxState } from 'ducks';
import { required } from 'lib/forms/validate';
import { DatePicker } from 'components/DatePicker/DatePicker';
import {
  getInitialGuestCount,
  selectCandidateProductInstances,
  selectGuestTypes,
} from 'lib/util/guestSelectUtils';
import {
  getPrices,
  Price,
  PromotionSummary,
  toProductShape,
  toPromotionSummary,
} from 'lib/util/productShape';
import { sortCalendarUnit } from 'lib/util/sortCalendarUnit';
import { WrappedFormHelper } from 'lib/forms/formHelpers';
import {
  selectIsPMP,
  selectLimitedAvailabilityThreshold,
  selectShowAvailabilitySearch,
  selectShowTotalBooked,
} from 'lib/util/privateMarketplace';
import { DateFormValues } from 'components/DateFormPanel/DateFormValues';
import { ProductPromotion, Product, GuestType } from 'models/product';
import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { getConfirmationTypeHelpText } from 'lib/util/getConfirmationTypeHelpText';
import { CalendarErrorPopup } from 'components/CalendarErrorPopup/CalendarErrorPopup';

import { GuestTypeList } from './GuestTypeList';
import { OtherParticipantsModal } from './OtherParticipantsModal';
import { useAttrByDateMap } from 'hooks/useAttrByDateMap';
import {
  attrMapHasFullyBookedDate,
  attrMapHasLimitedAvailabilityDate,
  attrMapHasPromotionDate,
} from 'lib/util/datePickerHelpers';
import { formattedTotalBookedText } from 'lib/util/formattedTotalBookedText';
import {
  getPerBookingMaximumParticipantsText,
  validatePerBookingParticipants,
} from 'lib/util/getEnablePerBookingMaximumParticipants';
import { ProductInstance } from 'models/productInstance';
import { getBookingStartDateTimeText } from 'lib/util/getBookingStartDateTimeText';
import { isStillNotOnSale } from 'lib/util/isStillNotOnSale';

import { BookingWidgetThemeContext } from 'contexts/BookingWidgetThemeContext';
import { getThemeCssOverride } from 'components/Themes';

import styles from './DateForm.module.css';

type Props = FormRenderProps<DateFormValues> & {
  showPrices: boolean;
  loading?: boolean;
  hideCalendar?: boolean;
  buttonLabel?: string;
  isAdd?: boolean;
  shouldShowPricingOnCalendar?: boolean;
};

const WrappedDayPicker = ({
  input,
  meta,
  hideCalendar,
  shouldShowPricingOnCalendar,
}: FieldRenderProps<string> & {
  product: Product;
}) => (
  <FormControl variant="standard" error={meta.touched && !!meta.error}>
    <DatePicker
      selectedDate={input.value || undefined}
      onDateChange={input.onChange}
      hideCalendar={hideCalendar}
      shouldShowPricingOnCalendar={shouldShowPricingOnCalendar}
    />
    {WrappedFormHelper({ touched: Boolean(meta.touched), error: meta.error })}
  </FormControl>
);

const getTotalGuestCount = (guestTypes: GuestType[], values: DateFormValues): number => {
  let total = 0;
  for (const guestType of guestTypes) {
    total += parseInt(String(lodashGet(values, `guestCount.${guestType.key}`) || '0'));
  }
  return total;
};

const validateProductInstanceUnits = (
  productInstance: ProductInstance,
  guestCount: Record<string, number>
): boolean => {
  if (productInstance.units?.some((unit) => unit.method === 'PER_BOOKING')) {
    return true;
  }

  for (const guestTypeKey of Object.keys(guestCount)) {
    if (productInstance.units?.some((unit) => unit.guest_type?.key === guestTypeKey)) {
      continue;
    }

    return false;
  }

  return true;
};

export const DateForm = ({
  handleSubmit,
  loading,
  invalid,
  showPrices,
  values,
  hideCalendar,
  buttonLabel,
  isAdd,
  shouldShowPricingOnCalendar = false,
}: Props) => {
  const { change, reset, setConfig } = useForm();
  const { submitting } = useFormState();
  const { t, i18n } = useTranslation();
  const router = useRouter();

  const { apiKey } = React.useContext(ApiKeyContext);

  const isUpgrade = Boolean(router.query.u);

  const [showOtherParticipantsModal, setShowOtherParticipantsModal] = React.useState(false);
  const [confirmationType, setConfirmationType] = React.useState<
    'REQUEST' | 'INSTANT' | 'REJECT' | null
  >(null);
  const [availabilityReason, setAvailabilityReason] = React.useState<
    'CLOSED' | 'EXCEED_DEADLINE' | 'NO_ALLOTMENT' | 'NO_MIN_PAX' | null
  >(null);
  const [confirmationTypeIsLoading, setConfirmationTypeIsLoading] = React.useState<boolean>(false);
  const [unitsError, setUnitsError] = React.useState('');
  const { date, guestCount, productInstanceId } = values;
  const guestCountTotal = guestCount
    ? Object.values(guestCount).reduce((a, b) => a + Number(b), 0)
    : 0;
  const productGuestTypes = useSelector((state: ReduxState) => selectGuestTypes(state, { t }));

  const product = useSelector((state: ReduxState) => state.server.product.product);

  const calendarUnitOrder = product?.booking_widget_settings?.calendar_unit_order ?? [];

  const [guestTypes, setGuestTypes] = React.useState<GuestType[]>(
    config.enableUnitDisplayOrder
      ? sortCalendarUnit([...productGuestTypes], calendarUnitOrder)
      : productGuestTypes
  );

  let defaultShowGuestCount = 3;
  if (config.enableUnitDisplayOrder) {
    if (calendarUnitOrder.some((g) => g.default_display_in_calendar)) {
      defaultShowGuestCount = 0;
      calendarUnitOrder.forEach((g) => {
        if (g.default_display_in_calendar) {
          defaultShowGuestCount++;
        }
      });
    }
  }

  const candidateProductInstances = useSelector((state: ReduxState) =>
    selectCandidateProductInstances(state, { date })
  );

  const productInstance = candidateProductInstances.find((p) => p.id === productInstanceId);

  React.useEffect(() => {
    if (productInstance) {
      const productInstanceGuestTypes =
        productInstance?.units?.map((unit) => unit.guest_type)?.filter((g) => Boolean(g)) ?? [];

      const sortGuestType = sortCalendarUnit([...productInstanceGuestTypes], calendarUnitOrder);
      if (config.enableUnitDisplayOrder) {
        if (sortGuestType.length > 0 && !lodashEqual(sortGuestType, guestTypes)) {
          setGuestTypes(sortGuestType);
        }
      } else {
        if (
          productInstanceGuestTypes.length > 0 &&
          !lodashEqual(productInstanceGuestTypes, guestTypes)
        ) {
          setGuestTypes(productInstanceGuestTypes);
        }
      }
    }
  }, [productInstance, productGuestTypes, guestTypes]);

  const isLotteryMode = product?.lottery_mode_settings?.is_enabled ?? false;

  const isPerBookingParticipantRuleActive =
    product?.booking_widget_settings?.is_per_booking_participant_rule_active ?? false;
  const perBookingMinimumParticipants =
    product?.booking_widget_settings?.per_booking_participant_rule?.minimum_participants ?? 0;
  const perBookingMaximumParticipants =
    product?.booking_widget_settings?.per_booking_participant_rule?.maximum_participants ?? 0;
  const perBookingParticipantsText = getPerBookingMaximumParticipantsText(
    perBookingMinimumParticipants,
    perBookingMaximumParticipants,
    t
  );

  const shouldShowTotalBooked = useSelector(selectShowTotalBooked);

  const [showExpandedPromotionPrices, setShowExpandedPromotionPrices] = React.useState<boolean>(
    false
  );

  const limitedAvailabilityThreshold = useSelector(selectLimitedAvailabilityThreshold);

  const attrMap = useAttrByDateMap();

  const hasLimitedAvailabilityDate = attrMapHasLimitedAvailabilityDate(attrMap);
  const hasPromotionDate = attrMapHasPromotionDate(attrMap);
  const hasFullyBookedDate = attrMapHasFullyBookedDate(attrMap);
  const customTopPageIsActive = useSelector(
    (state: ReduxState) => state.server.settings.all?.is_custom_top_page_enabled
  );

  const showAvailabilitySearch = useSelector(selectShowAvailabilitySearch);

  const isPMP = useSelector(selectIsPMP);

  const locale = i18n ? i18n.language : '';

  React.useEffect(() => {
    if (guestCount && Object.keys(guestCount).length > 0 && productInstanceId != null) {
      setConfirmationType(null);
      setAvailabilityReason(null);

      if (!validatePerBookingParticipants(guestCountTotal, product)) {
        setConfirmationTypeIsLoading(true);

        const searchParams = new URLSearchParams();

        const guestCounts: {
          guest_type_key: string;
          count: number;
        }[] = [];
        Object.keys(guestCount).forEach((key) => {
          guestCounts.push({
            guest_type_key: key,
            count: Number(guestCount[key]),
          });
        });
        searchParams.append('guest_counts', encodeURIComponent(JSON.stringify(guestCounts)));

        const url = `${
          config.apiUrl
        }/products/-/instances/${productInstanceId}?${searchParams.toString()}&include_resource_availability=true`;

        axios
          .get(url, {
            headers: { 'x-api-key': product?.booking_widget_api_key || apiKey },
          })
          .then((response) => {
            setConfirmationType(response.data.confirmation_type_for_given_guests ?? null);
            setAvailabilityReason(response.data.availability_reason_for_given_guests ?? null);
            setConfirmationTypeIsLoading(false);
          });
      }
    }
  }, [productInstanceId, guestCount]);

  React.useEffect(() => {
    if (
      !submitting &&
      productInstance &&
      !validateProductInstanceUnits(productInstance, values.guestCount)
    ) {
      setUnitsError(
        t('Selected date has different pricing. Please reselect number of participants.')
      );
    }
  }, [productInstance, values.guestCount, submitting]);

  React.useEffect(() => {
    if (
      candidateProductInstances.length > 0 &&
      (!productInstance ||
        !candidateProductInstances.some((instance) => instance.id === productInstance.id))
    ) {
      if (candidateProductInstances.length === 1) {
        change('productInstanceId', candidateProductInstances[0].id);
      } else {
        setConfirmationType(null);
        change('productInstanceId', null);
      }
    }
  }, [candidateProductInstances, change, productInstance]);

  const productShape = product && toProductShape(product);
  let prices: Price[] =
    productShape?.priceSchedules?.find((priceSchedule) => priceSchedule.prices != null)?.prices ??
    [];
  let promotions: PromotionSummary[] = (productShape && productShape.promotionSummaries) || [];
  if (productInstance && productInstance.units && productInstance.units.length > 0) {
    prices = getPrices(
      productInstance.units,
      undefined,
      productInstance.dynamic_pricing_adjusted_unit_prices
    );
    promotions = productInstance.promotions.map((promo: ProductPromotion) =>
      toPromotionSummary(promo, productInstance.units)
    );
  }

  const totalBooked = productShape?.totalBooked;

  const isModal = Boolean(router.query.isModal);

  const bookingWidgetTheme = React.useContext(BookingWidgetThemeContext);

  if (!product) return null;
  if (
    !product?.booking_widget_settings?.is_visible ||
    product?.booking_widget_settings?.is_view_only
  ) {
    return (
      <div className="widget-calendar__main">
        <p style={{ color: 'red' }}>{t('Sorry, this product is currently not available.')}</p>
      </div>
    );
  }

  if (isStillNotOnSale(product)) {
    return (
      <div className="widget-calendar__main">
        <p style={{ color: 'red' }}>{getBookingStartDateTimeText(product, t)}</p>
      </div>
    );
  }

  const isAnnualPass = product?.annual_pass_mode_settings?.is_enabled;

  const hiddenParticipantCount = getTotalGuestCount(
    guestTypes.slice(defaultShowGuestCount),
    values
  );

  const availabilitySearchUrl = customTopPageIsActive ? '/top/products#tour-list' : '/#tour-list';

  return (
    <>
      <div className="widget-calendar__main">
        {isModal && (
          <div className={clsx('widget-calendar__block', 'product__name')}>
            <p className="widget-calendar__title">{t('Product')}</p>
            {product.product_name ?? ''}
          </div>
        )}
        {shouldShowTotalBooked && Boolean(totalBooked) && (
          <p className="widget-calendar__booking">{formattedTotalBookedText(totalBooked, t)}</p>
        )}
        <form noValidate={true} onSubmit={handleSubmit}>
          <div className="widget-calendar__block">
            <p className="widget-calendar__title">{isAnnualPass ? t('User') : t('Participants')}</p>
            {guestTypes.length > 0 ? (
              <GuestTypeList
                guestTypes={guestTypes.slice(0, defaultShowGuestCount)}
                className="widget-calendar__number"
              />
            ) : null}
            {guestTypes.length > defaultShowGuestCount && (
              <div
                className="widget-calendar__number"
                onClick={() => setShowOtherParticipantsModal(true)}
              >
                <p className="widget-calendar__number__display">
                  {hiddenParticipantCount > 0
                    ? t('Other Participants - Total {{count}}', {
                        count: hiddenParticipantCount,
                      })
                    : t('See other price units')}
                </p>
                <OtherParticipantsModal
                  open={showOtherParticipantsModal}
                  onClose={() => setShowOtherParticipantsModal(false)}
                  guestTypes={guestTypes.slice(defaultShowGuestCount)}
                />
              </div>
            )}
          </div>
          {loading && (
            <Box width="100%" position="absolute">
              <Box width="100%" display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            </Box>
          )}
          {isPerBookingParticipantRuleActive && (
            <p style={{ marginBottom: '8px' }}>{perBookingParticipantsText}</p>
          )}
          <div className="widget-calendar__block">
            <p className="widget-calendar__title">
              {isAnnualPass ? t('Select Start Date') : t('Select Date and Time')}
            </p>
            <Field
              name="date"
              validate={required}
              label={t('Date')}
              component={WrappedDayPicker}
              product={product}
              hideCalendar={hideCalendar}
              shouldShowPricingOnCalendar={shouldShowPricingOnCalendar}
            />
          </div>
          {!isAnnualPass && (
            <div className="widget-calendar__comment">
              <div className="widget-calendar__comment__item">
                <p className="widget-calendar__comment__item__color blue" />
                <p className="widget-calendar__comment__item__name">{` : ${t('Instant')}`}</p>
                <p className="widget-calendar__comment__item__ic">?</p>
                <p className="widget-calendar__comment__item__info last">
                  {t(
                    'Instant bookings will be confirmed automatically within seconds of submission'
                  )}
                </p>
              </div>
              <div className="widget-calendar__comment__item">
                <p className="widget-calendar__comment__item__color yellow" />
                <p className="widget-calendar__comment__item__name">{` : ${
                  isLotteryMode ? t('Lucky Draw') : t('Request')
                }`}</p>
                <p className="widget-calendar__comment__item__ic">?</p>
                <p className="widget-calendar__comment__item__info first">
                  {isLotteryMode
                    ? t('Reservation will be confirmed after a drawing by supplier.')
                    : t('Request-only bookings require explicit confirmation from supplier')}
                </p>
              </div>
              {hasFullyBookedDate && (
                <div className="widget-calendar__comment__item">
                  <p
                    className={clsx(
                      'widget-calendar__comment__item__color',
                      'fully_booked',
                      getThemeCssOverride(bookingWidgetTheme, 'fully_booked')
                    )}
                  />
                  <p className="widget-calendar__comment__item__name">{` : ${t(
                    'Fully Booked'
                  )}`}</p>
                </div>
              )}
              {hasPromotionDate && (
                <div className="widget-calendar__comment__item">
                  <p className="widget-calendar__comment__item__color promotion" />
                  <p className="widget-calendar__comment__item__name">{` : ${t('Campaign')}`}</p>
                  <p className="widget-calendar__comment__item__ic">?</p>
                  <p className="widget-calendar__comment__item__info first">
                    {t('One or more campaigns apply to bookings on this date')}
                  </p>
                </div>
              )}
              {hasLimitedAvailabilityDate && limitedAvailabilityThreshold ? (
                <div className="widget-calendar__comment__item">
                  <p className="widget-calendar__comment__item__color few">
                    <img src="/static/images/ic_few.svg" alt="limited availability" />
                  </p>
                  <p className="widget-calendar__comment__item__name">{` : ${t(
                    'Limited Spots'
                  )}`}</p>
                  <p className="widget-calendar__comment__item__ic">?</p>
                  <p className="widget-calendar__comment__item__info first">
                    {t('Remaining availability is limited on this date')}
                  </p>
                </div>
              ) : (
                <div className="widget-calendar__comment__item" />
              )}
            </div>
          )}

          {!isAnnualPass && (
            <div className="widget-calendar__time">
              <p className="widget-calendar-selectTitle">{t('Time')}</p>
              <label className="widget-calendar-select">
                <Field
                  component="select"
                  disabled={candidateProductInstances.length === 0}
                  className="widget-calendar-selectRow__item"
                  validate={required}
                  name="productInstanceId"
                >
                  {candidateProductInstances.length > 1 && (
                    <option value="">{t('Please Select')}</option>
                  )}
                  {candidateProductInstances.length > 0 ? (
                    candidateProductInstances.map((p) => (
                      <option key={p.id} value={p.id}>
                        {`${moment(p.start_time_local, 'H:mm').locale(locale).format('LT')}${
                          p.start_time_description ? ` ${p.start_time_description}` : ''
                        }`}
                      </option>
                    ))
                  ) : (
                    <option value="">{t('Select a Date')}</option>
                  )}
                </Field>
              </label>
              {confirmationTypeIsLoading && (
                <p className="widget-calendar__time__badge__loader">
                  <CircularProgress size={20} />
                </p>
              )}
              {confirmationType === 'INSTANT' && (
                <p className="widget-calendar__time__badge instant">{t('Instant')}</p>
              )}
              {confirmationType === 'REQUEST' &&
                (isUpgrade ? (
                  <p className="widget-calendar__time__badge unavailable">
                    {t('Unavailable Online')}
                  </p>
                ) : (
                  <p className="widget-calendar__time__badge request">
                    {isLotteryMode ? t('Lucky Draw') : t('Request')}
                  </p>
                ))}
              {confirmationType === 'REJECT' && (
                <>
                  <p className="widget-calendar__time__badge unavailable">{t('Unavailable')}</p>
                  <p className="widget-calendar__comment__item__info first">
                    {getConfirmationTypeHelpText('REJECT', availabilityReason, t)}
                  </p>
                </>
              )}
            </div>
          )}

          {isUpgrade && candidateProductInstances.length === 0 && (
            <p className="widget-calendar-error">{t('No availability for your date')}</p>
          )}

          <button
            disabled={
              invalid ||
              (guestTypes.length > 0 && guestCountTotal === 0) ||
              confirmationType === 'REJECT' ||
              (isUpgrade && confirmationType === 'REQUEST') ||
              (isAdd && confirmationType === 'REQUEST') ||
              validatePerBookingParticipants(guestCountTotal, product)
            }
            type="submit"
            className="widget-calendar__button"
          >
            {buttonLabel || t('Next')}
          </button>

          {showAvailabilitySearch && isPMP && (
            <Box mt={2}>
              <p>
                {i18n.language === 'en-US' && (
                  <>
                    {`Didn't find the date and time you were looking for? Try `}
                    <Link
                      className={clsx('maincolor-txt', styles['link'])}
                      href={availabilitySearchUrl}
                    >
                      product availability search.
                    </Link>
                  </>
                )}
              </p>
              <p>
                {i18n.language === 'ja-JP' && (
                  <>
                    {`ご希望の日程に空きがありませんでしたか？ `}
                    <Link
                      className={clsx('maincolor-txt', styles['link'])}
                      href={availabilitySearchUrl}
                    >
                      空き状況を検索する
                    </Link>
                  </>
                )}
              </p>
            </Box>
          )}

          {unitsError && (
            <CalendarErrorPopup
              text={unitsError}
              onClose={() => {
                setUnitsError('');
                setConfig('keepDirtyOnReinitialize', false);
                reset({
                  ...values,
                  guestCount: getInitialGuestCount(guestTypes, product),
                });
                setConfig('keepDirtyOnReinitialize', true);
              }}
            />
          )}
        </form>
      </div>
      {showPrices &&
        (promotions.length > 0 ? (
          <PromotionPanel
            promotions={promotions}
            onClickExpanded={() => setShowExpandedPromotionPrices(true)}
          />
        ) : (
          <PricePanel prices={prices} shouldShowPricing={shouldShowPricingOnCalendar} />
        ))}
      {showExpandedPromotionPrices && (
        <Modal open onClose={() => setShowExpandedPromotionPrices(false)}>
          <PromotionPanel promotions={promotions} expanded={true} />
        </Modal>
      )}
    </>
  );
};
